import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers/fetch.js';
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useClientSideRows } from "../../datagrid/hooks/useClientSideRows.js";
import queryClient from '../../../../query_client.js';
import { useGanttUpdates } from './useGanttUpdates.js';

import {
  task_links_path
} from '~/routes';

const date10 = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v.slice(0, 10);
  if (v && typeof v === "object" && v.label) return String(v.label).slice(0, 10);
  return null;
};

export function useGanttView({ editMode, filterModel, quickFilterText, fetchPath, fetchBody, projectId, permissions }) {
  const editModeRef = useRef(editMode);
  const { updateTask, addLink, deleteLink } = useGanttUpdates({projectId});
  const openIdsRef = useRef(new Set());
  const baseKey = 'taskClientSideRows';
  const { rowData, loading: rowsLoading } = useClientSideRows({
    baseKey,
    fetchPath,
    fetchBody,
    quickFilterText,
    filterModel,
    sortModel: [{ sort: 'asc', colId: 'start_date' }, { sort: 'asc', colId: 'end_date' }]
  })
  const updatedTasksRef = useRef(false);
  const tasksKey = [baseKey, fetchPath];

  const linksKey = ['taskProjectLinks', projectId];
  const {data: linksData, loading: linksLoading} = useQuery({
    queryKey: linksKey,
    queryFn: () => fetchJson(task_links_path({ project_id: projectId })),
    enabled: !!projectId,
    staleTime: 30_000,
    keepPreviousData: true
  });

  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  const isTaskId = (id) => {
    return !String(id).startsWith('wp:');
  };

  const refreshIfUpdated = useCallback(() => {
    if (updatedTasksRef.current) {
      queryClient.refetchQueries({ queryKey: tasksKey });
      updatedTasksRef.current = false;
    }
  }, []);

  const wpUnassignedId = "wp:unassigned";
  const unassignedWpLabel = I18n.t('unassigned');

  const init = (api) => {
    const previousTasks = new Map();
    const revertingTasks = new Set();

    api.intercept('drag-task', ({id}) => id !== wpUnassignedId && editModeRef.current && permissions.update_task);

    api.intercept('update-task', ({id}) => id !== wpUnassignedId && editModeRef.current && permissions.update_task);

    api.intercept('update-link', ({ link }) => {
      if (!permissions?.create_link) return false;
      if (!editModeRef.current) return false;
      if (!isTaskId(link.source) || !isTaskId(link.target)) return false;
    });

    api.intercept('delete-link', async ({id}) => {
      if (!permissions?.create_link) return false;
      if (!editModeRef.current) return false;

      const link = await deleteLink({ id });
      if (!link) return false;

      queryClient.setQueryData(linksKey, (old) => {
        const arr = (old?.links || old || []);
        const next = arr.filter((l) => l.id !== id);
        return old?.links ? { ...old, links: next } : next;
      });
      refreshIfUpdated();

      return false;
    });

    api.intercept('add-link', async ({ link }) => {
      if (!permissions?.create_link) return false;
      if (!editModeRef.current) return false;

      if (!isTaskId(link.source) || !isTaskId(link.target)) {
        return false;
      };

      const newLink = await addLink({ link });
      if (!newLink) return false;

      queryClient.setQueryData(linksKey, (old) => {
        const arr = (old?.links || old || []);
        const next = [...arr, newLink];
        return old?.links ? { ...old, links: next } : next;
      });
      refreshIfUpdated();

      return false;
    });

    api.on('drag-task', (params) => {
      if (!permissions.update_task) return false;
      const { id, inProgress } = params;
      if (!inProgress || previousTasks.has(id)) return;

      const t = api.getTask(id);
      if (!t) return;

      const hasValidStart = t.start && !Number.isNaN(new Date(t.start).getTime());

      if (!hasValidStart) {
        const state = api.getState?.();

        const viewStart = state?._scales?.start;
        const end = new Date(viewStart);
        end.setDate(end.getDate() + 7);

        api.exec('update-task', {
          id,
          force: true,
          task: {
            ...t,
            start: viewStart,
            end
          }
        });
      }

      const updated = api.getTask(id);

      previousTasks.set(id, {
        start: updated.start,
        end: updated.end,
        duration: updated.duration
      });
    });

    api.on('update-task', async ({ id, task, inProgress, force }) => {
      if (inProgress && !force) return;

      if (revertingTasks.has(id)) {
        revertingTasks.delete(id);
        return;
      }

      const prev = previousTasks.get(id);
      try {
        await updateTask({ id, task });
        previousTasks.delete(id);
        queryClient.invalidateQueries({ queryKey: tasksKey, refetchType: 'none' });
        queryClient.invalidateQueries({ queryKey: linksKey, refetchType: 'none' });
        updatedTasksRef.current = true;
      } catch (e) {
        if (prev) {
          revertingTasks.add(id);
          api.exec('update-task', {id, task: prev});
        }
        previousTasks.delete(id);
      }
    });

    api.on('open-task', ({ id, mode }) => {
      if (mode) openIdsRef.current.add(id);
      else openIdsRef.current.delete(id);
    });
  }

  const gantt = useMemo(() => {
    const rows = Array.isArray(rowData) ? rowData : [];
    const items = rows.filter((r) => r && r.group !== true);

    const wpIndex = items.reduce(
      (acc, r) => {
        const wpId = r.work_package_id;
        const key = wpId != null ? `wp:${wpId}` : wpUnassignedId;

        acc.counts.set(key, (acc.counts.get(key) || 0) + 1);
        if (!acc.first.has(key)) acc.first.set(key, r);

        return acc;
      },
      { counts: new Map(), first: new Map() },
    );

    const wpTasks = Array.from(wpIndex.counts.keys()).map((key) => {
      const r = wpIndex.first.get(key) || {};
      const wp = r.work_package || {};

      const start = date10(wp.start_date);
      const end = date10(wp.end_date);

      const progress = wp.completion_percentage || 0;

      const s = {
        id: key,
        progress,
        text: r.work_package_id != null ? wp.label : unassignedWpLabel,
        type: "work-package",
        estimated_work_days: wp.estimated_work_days || undefined,
        description: wp.description,
        open: openIdsRef.current.has(key)
      };

      if (start) s.start = start;
      if (end) s.end = end;

      s.dates = start && end
        ? `${start} - ${end}`
        : start || end || undefined;

      return s;
    });

    const tasks = items.map((r) => {
      const wpId = r.work_package_id;
      const parent = wpId != null ? `wp:${wpId}` : wpUnassignedId;

      const progress = (r.completion_percentage && r.completion_percentage.label) || 0;

      const baseType = `task-${r.status && r.status.status}`;
      const taskType = r.task_type?.label || undefined;
      let type = baseType;
      if (taskType === 'checkpoint') {
        type = 'milestone';
      }

      const t = {
        id: r.id,
        parent,
        progress,
        text: String((r.title && r.title.label) || (r.identifier && r.identifier.label) || r.id),
        identifier: r.identifier && r.identifier.label,
        type: type,
        estimated_work_days: r.estimated_work_days.label || undefined,
        status: r.status.status,
        status_label: r.status.label,
        description: r.description.label,
        phase: r.phase_id?.label
      };

      const start = date10(r.start_date);
      const end = date10(r.end_date);

      if (start) t.start = start;
      if (end) t.end = end;

      t.dates = start && end
        ? `${start} - ${end}`
        : start || end || undefined;

      return t;
    });

    return {
      tasks: [...wpTasks, ...tasks],
      links: linksData,
    };
  }, [rowData, linksData]);

  return { ...gantt, loading: rowsLoading || linksLoading, init };
}
